import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AgentThread,
  GetAgentThreadsResponse,
  AssistantTodoItem,
  GetAllAssistantTodosResponse,
} from '@dreamai/types';
import api from '../../../api';
import NewThreadModal from './NewThreadModal';
import { ReactComponent as CampaignsIcon } from '../../../icons/campaigns.svg';
import { ReactComponent as HourglassIcon } from '../../../icons/hourglass.svg';
import { ReactComponent as PlusIcon } from '../../../icons/plus.svg';
import './campaigns-drawer.scss';

interface Props {
  open: boolean;
  agentId: string | undefined;
  agentName?: string;
  agentImageUrl?: string;
  agentType?: string;
  onClose: () => void;
  /** Element that toggles the drawer; click-outside detection ignores it
   *  so toggling doesn't immediately close-then-reopen. */
  toggleRef?: React.RefObject<HTMLElement | null>;
}

interface CampaignWithStatus {
  thread: AgentThread;
  /** The most important active sub-thread status line, if any */
  statusLine: string | null;
  statusType: 'waiting' | 'processing' | null;
}

// ── Component ─────────────────────────────────────────────────────────────

/**
 * Sliding drawer that appears next to the ExpansiveSideNavigation and
 * lists the user's campaigns ordered by created_at desc. Mirrors the
 * ProjectsDrawer layout but fetches Campaign-kind threads.
 */
const CampaignsDrawer: React.FC<Props> = ({
  open,
  agentId,
  agentName,
  agentImageUrl,
  agentType,
  onClose,
  toggleRef,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [campaigns, setCampaigns] = useState<CampaignWithStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const drawerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch campaign threads and todos in parallel
      const [threadsRes, todosRes] = await Promise.all([
        api.get<GetAgentThreadsResponse>('/agents/threads', {
          params: {
            agent_id: agentId,
            kind: 'Campaign',
            excludePinned: 'true',
            excludeShared: 'true',
            excludeSubThreads: 'true',
          },
          ...authHeaders,
        }),
        api.get<GetAllAssistantTodosResponse>(
          `/todos?agentId=${encodeURIComponent(agentId)}`,
          authHeaders,
        ),
      ]);

      const threads = threadsRes.data.success ? threadsRes.data.threads : [];
      const todos = todosRes.data.todos || [];

      // Group todos by parent thread
      const todosByParent = new Map<string, AssistantTodoItem[]>();
      todos.forEach((todo) => {
        if (!todo.parentThreadId) return;
        const list = todosByParent.get(todo.parentThreadId) || [];
        list.push(todo);
        todosByParent.set(todo.parentThreadId, list);
      });

      // Build campaign list with status, sorted by created_at desc
      const campaignList: CampaignWithStatus[] = threads
        .filter((t) => !t.is_archived_thread)
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .map((thread) => {
          const subs = todosByParent.get(thread.id) || [];
          // Find the most important active sub-thread
          const waitingSub = subs.find((s) => s.status === 'waiting_on_user');
          const processingSub = subs.find(
            (s) =>
              s.status === 'processing' ||
              s.status === 'waiting_on_third_party',
          );
          const activeSub = waitingSub || processingSub;

          let statusLine: string | null = null;
          let statusType: 'waiting' | 'processing' | null = null;

          if (activeSub) {
            // Use the active step description, falling back to summary/description
            const steps = activeSub.steps || [];
            const activeStep =
              steps.find((s) => s.active) ||
              steps.find((s) => s.status === 'in_progress') ||
              steps.find((s) => s.status === 'waiting_on_user');
            statusLine =
              activeStep?.description ||
              activeSub.summary ||
              activeSub.description;
            statusType = waitingSub ? 'waiting' : 'processing';
          }

          return { thread, statusLine, statusType };
        });

      setCampaigns(campaignList);
      setHasFetched(true);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      setError('Could not load campaigns.');
    } finally {
      setLoading(false);
    }
  }, [agentId, getAccessTokenSilently]);

  // Fetch (or re-fetch) every time the drawer opens.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      fetchCampaigns();
    }
    prevOpenRef.current = open;
  }, [open, fetchCampaigns]);

  // Close on click outside (ignoring the toggle button).
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (drawerRef.current && drawerRef.current.contains(target)) return;
      if (toggleRef?.current && toggleRef.current.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, onClose, toggleRef]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  const handleSelect = (threadId: string) => {
    window.location.assign(
      `/conversations?thread_id=${encodeURIComponent(threadId)}`,
    );
  };

  return (
    <>
      {agentId && (
        <NewThreadModal
          isOpen={showNewCampaignModal}
          onClose={() => setShowNewCampaignModal(false)}
          agentId={agentId}
          agentName={agentName}
          agentImageUrl={agentImageUrl}
          agentType={agentType}
          creationType='campaign'
          onThreadCreated={() => {
            fetchCampaigns();
          }}
        />
      )}
      {createPortal(
        <AnimatePresence>
          {open && (
            <React.Fragment key='campaigns-drawer-fragment'>
              <motion.div
                key='campaigns-drawer-shroud'
                className='campaigns-drawer-shroud'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                onClick={onClose}
              />
              <motion.aside
                ref={drawerRef}
                key='campaigns-drawer'
                className='campaigns-drawer'
                initial={{ x: -16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -16, opacity: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 420,
                  damping: 34,
                  mass: 0.6,
                }}
              >
                <div className='campaigns-drawer-header'>
                  <button
                    type='button'
                    className='drawer-mobile-close'
                    onClick={onClose}
                    aria-label='Close'
                  >
                    ← Back
                  </button>
                  <div className='campaigns-drawer-header-top'>
                    <CampaignsIcon className='campaigns-drawer-header-icon' />
                    {agentId && (
                      <button
                        type='button'
                        className='campaigns-drawer-new-btn'
                        onClick={() => setShowNewCampaignModal(true)}
                      >
                        <PlusIcon className='campaigns-drawer-new-btn-icon' />
                        New
                      </button>
                    )}
                  </div>
                  <h2 className='campaigns-drawer-title'>Campaigns</h2>
                </div>

                <div ref={scrollRef} className='campaigns-drawer-scroll'>
                  {error && (
                    <div className='campaigns-drawer-error'>{error}</div>
                  )}
                  {!error &&
                    campaigns.length === 0 &&
                    !loading &&
                    hasFetched && (
                      <div className='campaigns-drawer-empty'>
                        No campaigns yet.
                      </div>
                    )}
                  {campaigns.map((c) => (
                    <button
                      key={c.thread.id}
                      type='button'
                      className='campaigns-drawer-item'
                      onClick={() => handleSelect(c.thread.id)}
                    >
                      <div className='campaigns-drawer-item-img'>
                        {c.thread.icon_image_url ? (
                          <img src={c.thread.icon_image_url} alt='' />
                        ) : (
                          <div className='campaigns-drawer-item-img-placeholder' />
                        )}
                      </div>
                      <div className='campaigns-drawer-item-content'>
                        <div className='campaigns-drawer-item-title'>
                          {c.thread.description || 'Untitled Campaign'}
                        </div>
                        {c.statusLine && (
                          <div
                            className={`campaigns-drawer-item-status ${c.statusType || ''}`}
                          >
                            <HourglassIcon className='campaigns-drawer-status-icon' />
                            <span>{c.statusLine}</span>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                  {loading && (
                    <div className='campaigns-drawer-loading'>Loading…</div>
                  )}
                </div>
              </motion.aside>
            </React.Fragment>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default CampaignsDrawer;
